import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
} from '@mui/material';
import {
  Business,
  Person,
  CheckCircle,
  PhoneAndroid,
  Store,
  Notifications,
  TrendingUp,
  Support,
} from '@mui/icons-material';
import { Style } from './Style';

const HowItHelps = () => {
  const serviceProviderSteps = [
    'Go to the Google Play Store and download our app',
    'Sign up with your mobile number',
    'Add your services, specifying if you offer slots or walk-in services',
    'See a complete list of your booked clients right in your app',
    'Check them in with a tap when they arrive'
  ];

  const customerBenefits = [
    'Book appointments using a simple QR code',
    'Book directly through WhatsApp',
    'Download our app from the Play Store',
    'Book your service directly in the app',
    'Get wait time notifications for walk-ins',
    'See current running slots in real-time'
  ];

  return (
<Box id="howItHelps" sx={{ py: 8, backgroundColor: '#f8f9fa' }}>      <Container maxWidth="lg">
        <Typography
          variant="h3"
          component="h2"
          textAlign="center"
          gutterBottom
          sx={{
            fontWeight: 'bold',
            color: 'primary.main',
            mb: 2,
          }}
        >
          How It Helps
        </Typography>
        
        <Typography
          variant="h6"
          textAlign="center"
          sx={{
            color: 'text.secondary',
            mb: 6,
            maxWidth: '800px',
            mx: 'auto',
            lineHeight: 1.6,
          }}
        >
          Our service takes the hassle out of scheduling, offering a modern solution 
          that benefits both service providers and their customers.
        </Typography>

        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Card
              elevation={4}
              sx={{
                height: '100%',
                borderRadius: 3,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Business sx={{ fontSize: 50, mr: 2 }} />
                  <Typography variant="h4" fontWeight="bold">
                    For Service Providers
                  </Typography>
                </Box>
                
                <Typography variant="h6" sx={{ mb: 3, opacity: 0.9 }}>
                  Getting started with our service is simple and easy:
                </Typography>

                <List>
                  {serviceProviderSteps.map((step, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemIcon>
                        <CheckCircle sx={{ color: 'white' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={step}
                        primaryTypographyProps={{
                          color: 'white',
                          fontSize: '0.95rem'
                        }}
                      />
                    </ListItem>
                  ))}
                </List>

                <Paper
                  elevation={2}
                  sx={{
                    p: 2,
                    mt: 3,
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                    Streamline your workflow and improve your service with our 
                    easy-to-use check-in system.
                  </Typography>
                </Paper>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card
              elevation={4}
              sx={{
                height: '100%',
                borderRadius: 3,
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                color: 'white',
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Person sx={{ fontSize: 50, mr: 2 }} />
                  <Typography variant="h4" fontWeight="bold">
                    For Customers
                  </Typography>
                </Box>
                
                <Typography variant="h6" sx={{ mb: 3, opacity: 0.9 }}>
                  Say goodbye to long phone calls and frustrating wait times:
                </Typography>

                <List>
                  {customerBenefits.map((benefit, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemIcon>
                        <CheckCircle sx={{ color: 'white' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={benefit}
                        primaryTypographyProps={{
                          color: 'white',
                          fontSize: '0.95rem'
                        }}
                      />
                    </ListItem>
                  ))}
                </List>

                <Paper
                  elevation={2}
                  sx={{
                    p: 2,
                    mt: 3,
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                    Book anytime, anywhere with complete flexibility and real-time updates.
                  </Typography>
                </Paper>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box sx={{ mt: 8 }}>
          <Typography
            variant="h4"
            textAlign="center"
            gutterBottom
            sx={{
              fontWeight: 'bold',
              color: 'primary.main',
              mb: 4,
            }}
          >
            Key Benefits
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={3}
                sx={{
              ...Style.HelpPaperCommon
                }}
              >
                <PhoneAndroid sx={{ fontSize: 50, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" fontWeight="bold">
                  Mobile First
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Designed for mobile devices with intuitive user experience
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={3}
                 sx={{
              ...Style.HelpPaperCommon
                }}
              >
                <Store sx={{ fontSize: 50, color: 'success.main', mb: 2 }} />
                <Typography variant="h6" fontWeight="bold">
                  Business Growth
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Help businesses optimize schedules and reduce no-shows
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={3}
                sx={{
              ...Style.HelpPaperCommon
                }}
              >
                <Notifications sx={{ fontSize: 50, color: 'warning.main', mb: 2 }} />
                <Typography variant="h6" fontWeight="bold">
                  Real-time Updates
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Get instant notifications about wait times and availability
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={3}
               sx={{
              ...Style.HelpPaperCommon
                }}
              >
                <Support sx={{ fontSize: 50, color: 'secondary.main', mb: 2 }} />
                <Typography variant="h6" fontWeight="bold">
                  24/7 Support
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Round-the-clock customer support for all your needs
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default HowItHelps;

